class DateUtils {
	static getDateMonthsAgo = months => {
		const date = new Date();

		date.setMonth(date.getMonth() - months);

		return date;
	};

	static formatDateToAmerican = date => {
		if (!date) {
			return "";
		}

		if (date instanceof Date) {
			const year = date.getFullYear();
			const month = String(date.getMonth() + 1).padStart(2, "0");
			const day = String(date.getDate()).padStart(2, "0");

			return `${year}-${month}-${day}`;
		}

		if (typeof date !== "string") {
			return "";
		}

		if (date.includes("-")) {
			return date;
		}

		const [day, month, year] = date.split("/");

		return `${year}-${month}-${day}`;
	};

	static isDateEmptyOrInvalid = date => {
		return (
			!date ||
			Number.isNaN(new Date(date).getTime())
		);
	};

	static normalizeDateOnly = date => {
		if (!date) {
			return null;
		}

		const parsedDate = date instanceof Date
			? date
			: new Date(date);

		if (Number.isNaN(parsedDate.getTime())) {
			return null;
		}

		return new Date(
			parsedDate.getFullYear(),
			parsedDate.getMonth(),
			parsedDate.getDate()
		);
	};

	static formatDateToBrazilian = date => {
		if (!date) {
			return "-";
		}

		if (date instanceof Date) {
			if (Number.isNaN(date.getTime())) {
				return "-";
			}

			return date.toLocaleDateString("pt-BR");
		}

		if (typeof date !== "string") {
			return "-";
		}

		const normalizedDate = date.trim();

		if (!normalizedDate) {
			return "-";
		}

		if (/^\d{2}\/\d{2}\/\d{4}$/.test(normalizedDate)) {
			return normalizedDate;
		}

		if (/^\d{4}-\d{2}-\d{2}$/.test(normalizedDate)) {
			const [year, month, day] = normalizedDate.split("-");

			return `${day}/${month}/${year}`;
		}

		const parsedDate = new Date(normalizedDate);

		if (Number.isNaN(parsedDate.getTime())) {
			return "-";
		}

		return parsedDate.toLocaleDateString("pt-BR");
	};

	static isDateAfter = (date, referenceDate = new Date()) => {
		const normalizedDate = DateUtils.normalizeDateOnly(
			DateUtils.formatDateToAmerican(date)
		);

		const normalizedReferenceDate = DateUtils.normalizeDateOnly(
			DateUtils.formatDateToAmerican(referenceDate)
		);

		if (!normalizedDate || !normalizedReferenceDate) {
			return false;
		}

		return normalizedDate > normalizedReferenceDate;
	};

	static isDateEqual = (date, referenceDate = new Date()) => {
		const normalizedDate = DateUtils.normalizeDateOnly(
			DateUtils.formatDateToAmerican(date)
		);

		const normalizedReferenceDate = DateUtils.normalizeDateOnly(
			DateUtils.formatDateToAmerican(referenceDate)
		);

		if (!normalizedDate || !normalizedReferenceDate) {
			return false;
		}

		return normalizedDate.getTime() === normalizedReferenceDate.getTime();
	};

	static isDateAfterToday = date => {
		return DateUtils.isDateAfter(date);
	};

	static isDateAfterOrEqual = (date, referenceDate = new Date()) => {
		return (
			DateUtils.isDateAfter(date, referenceDate) ||
			DateUtils.isDateEqual(date, referenceDate)
		);
	};

	static getDateDaysFromToday = daysAhead => {
		const date = new Date();

		date.setDate(date.getDate() + Number(daysAhead || 0));

		return DateUtils.normalizeDateOnly(date);
	};

	static parseDateOnlyToTimestamp = date => {
		const normalizedDate = DateUtils.normalizeDateOnly(
			DateUtils.formatDateToAmerican(date)
		);

		return normalizedDate ? normalizedDate.getTime() : null;
	};

	static countBusinessDays = (startDate, endDate) => {
		const start = DateUtils.normalizeDateOnly(startDate);
		const end = DateUtils.normalizeDateOnly(endDate);

		if (!start || !end || end < start) {
			return 0;
		}

		let businessDays = 0;
		const currentDate = new Date(start);

		while (currentDate <= end) {
			const dayOfWeek = currentDate.getDay();

			if (dayOfWeek !== 0 && dayOfWeek !== 6) {
				businessDays += 1;
			}

			currentDate.setDate(currentDate.getDate() + 1);
		}

		return businessDays;
	};

	static formatDateToDayAndMonth = date => {
		const normalizedDate = DateUtils.normalizeDateOnly(date);

		if (!normalizedDate) {
			return "-";
		}

		const formattedDate = normalizedDate.toLocaleDateString("pt-BR", {
			day: "numeric",
			month: "long"
		});

		return formattedDate.replace(
			/ de ([a-záéíóúãõç]+)/,
			(_, month) => ` de ${month.charAt(0).toUpperCase()}${month.slice(1)}`
		);
	};
	
	static formatMonthYear = value => {
		const numbers = String(value || "").replace(/\D/g, "").slice(0, 6);

		if (numbers.length <= 2) {
			return numbers;
		}

		return `${numbers.slice(0, 2)}/${numbers.slice(2)}`;
	};
}

export default DateUtils;